'use client'
import { usePathname } from 'next/navigation'
import { cloneElement, createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

import type { HTMLAttributes, JSX } from 'react'

// Body scroll-lock shared by every Drawer instance through a module-level count. The first lock
// captures the scroll position and pins the body; the last unlock removes the pin and restores it.
// The count is mutated synchronously, unlike the previous approach of counting [data-name="drawer"]
// DOM nodes with a MutationObserver, which raced with the 310ms close animation (nodes linger in
// the DOM after close) and could leave the body locked forever (SUP0003-657: a leaked
// position:fixed on <body> blanked everything below the header).
let bodyLockCount = 0
let lockedScrollY = 0

const lockBodyScroll = () => {
  if (bodyLockCount === 0) {
    lockedScrollY = window.scrollY
    document.body.setAttribute('data-body-scroll-lock', 'true')
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${lockedScrollY}px`
    document.body.style.width = '100%'
  }

  bodyLockCount += 1
}

const unlockBodyScroll = () => {
  if (bodyLockCount === 0) return

  bodyLockCount -= 1

  if (bodyLockCount === 0) {
    document.body.removeAttribute('data-body-scroll-lock')
    // Remove only the properties we set; the Mitel webchat script keeps its own inline body styles.
    document.body.style.removeProperty('overflow')
    document.body.style.removeProperty('position')
    document.body.style.removeProperty('top')
    document.body.style.removeProperty('width')
    window.scrollTo(0, lockedScrollY)
  }
}

type DrawerContextProps = {
  isOpen: boolean
  setOpen: (open: boolean) => void
  overlayClose?: () => void
  direction: DirectionEnum
  handleDrawerOpen: () => void
  handleDrawerClose: () => void
}

type DirectionEnum = 'left' | 'right' | 'top' | 'bottom'

const defaultDrawerContextProps = {
  isOpen: false,
  setOpen: () => {},
  overlayClose: () => {},
  direction: 'left' as DirectionEnum,
  handleDrawerOpen: () => {},
  handleDrawerClose: () => {},
}

const DrawerContext = createContext<DrawerContextProps>(defaultDrawerContextProps)
const useDrawerContext = () => useContext(DrawerContext)
const DrawerProvider = ({ value, children }: { value: DrawerContextProps; children: React.ReactNode }) => {
  return <DrawerContext.Provider value={value}>{children}</DrawerContext.Provider>
}

export const Drawer = ({
  open,
  onOpenChange,
  overlayClose,
  direction = 'left',
  children,
}: {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  overlayClose?: () => void
  direction?: DirectionEnum
  children: React.ReactNode
}) => {
  const [isOpen, setOpen] = useState(open ?? false)
  const pathname = usePathname()

  const handleDrawerOpen = useCallback(() => {
    if (isOpen) return

    setOpen(true)
    onOpenChange?.(true)
  }, [isOpen, onOpenChange])

  const handleDrawerClose = useCallback(() => {
    if (!isOpen) return

    setOpen(false)
    onOpenChange?.(false)
  }, [isOpen, onOpenChange])

  // React owns the lock's full lifecycle: the cleanup runs when the drawer closes and when it
  // unmounts, so a lock can never outlive the drawer that set it.
  useEffect(() => {
    if (!isOpen) return

    lockBodyScroll()

    return () => unlockBodyScroll()
  }, [isOpen])

  // Controlled mode: mirror the `open` prop into local state.
  useEffect(() => {
    if (typeof open === 'undefined') return

    setOpen(open)
  }, [open])

  // Close on navigation so persistent drawers (mobile menu, cart) never keep the body locked on
  // the next page. The ref keeps this effect from firing when the handler changes identity.
  const closeRef = useRef(handleDrawerClose)
  closeRef.current = handleDrawerClose
  const didMountRef = useRef(false)
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true

      return
    }

    // The new page starts at the top, so restoring the previous page's scroll offset would be wrong.
    lockedScrollY = 0
    closeRef.current()
  }, [pathname])

  return <DrawerProvider value={{ isOpen, setOpen, direction, handleDrawerOpen, handleDrawerClose, overlayClose }}>{children}</DrawerProvider>
}

export const DrawerTrigger = ({ children }: { children: JSX.Element | undefined }) => {
  const { handleDrawerOpen } = useDrawerContext()

  if (!children) return null

  // extended the onClick to include the handleDrawerOpen function and any other onClick functions that may be passed in
  return cloneElement(children, {
    onClick: (e: React.MouseEvent) => {
      handleDrawerOpen()
      if (children.props.onClick) children.props.onClick(e)
    },
  })
}

Drawer.Trigger = DrawerTrigger

export const DrawerClose = ({ children }: { children: JSX.Element | undefined }) => {
  const { handleDrawerClose } = useDrawerContext()

  if (!children) return null

  return cloneElement(children, {
    onClick: () => {
      handleDrawerClose()
      if (children.props.onClick) children.props.onClick()
    },
  })
}

Drawer.Close = DrawerClose

export const DrawerPortal = ({ children }: { children: React.ReactNode }) => {
  const { isOpen } = useDrawerContext()
  const [animate, setAnimate] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setAnimate(true)
    } else {
      setTimeout(() => setAnimate(false), 310)
    }
  }, [isOpen])

  if (animate) {
    return createPortal(children, document.body)
  }

  return null
}

Drawer.Portal = DrawerPortal

const directionMap = {
  left: {
    default: 'left-0',
    open: 'translate-x-0',
    close: '-translate-x-full',
  },
  right: {
    default: 'right-0',
    open: 'translate-x-0',
    close: 'translate-x-full',
  },
  top: {
    default: 'top-0',
    open: 'translate-y-0',
    close: '-translate-y-full',
  },
  bottom: {
    default: 'bottom-0',
    open: 'translate-y-0',
    close: 'translate-y-full',
  },
}

export const DrawerContent = ({ className, children }: { className?: string; children: React.ReactNode }) => {
  const { isOpen, handleDrawerClose, direction, overlayClose } = useDrawerContext()
  const [animate, setAnimate] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => setAnimate(true), 30)
    } else {
      setAnimate(false)
    }
  }, [isOpen])

  return (
    <DrawerPortal>
      <div
        className={cn('fixed inset-0 bg-black transition-opacity duration-300', animate ? 'opacity-30' : 'opacity-0')}
        onClick={overlayClose ?? handleDrawerClose}
        onKeyDown={overlayClose ?? handleDrawerClose}
        role="none"
        style={{ zIndex: '16777220' }}
      />
      <div
        data-name="drawer"
        role="dialog"
        aria-modal="true"
        aria-hidden={!isOpen}
        // Adding this large zIndex to ensure that the drawer is above Mitel chat widget which has zIndex of 16777215
        style={{ zIndex: '16777221' }}
        className={cn(
          'ease-&lsqb;cubic-bezier(0.4,0,0.2,1)&rsqb; fixed shadow-lg transition-transform duration-300',
          directionMap[direction].default,
          animate ? directionMap[direction].open : directionMap[direction].close,
          direction === 'bottom' || direction === 'top' ? 'h-auto w-full' : 'inset-y-0 w-[90%] sm:w-[400px]',
        )}
      >
        <div className={cn('relative flex h-full flex-col', className)}>{children}</div>
      </div>
    </DrawerPortal>
  )
}

Drawer.Content = DrawerContent

const DrawerHeader = ({ children, ...rest }: HTMLAttributes<HTMLHeadElement>) => {
  return <header {...rest}>{children}</header>
}

Drawer.Header = DrawerHeader

const DrawerTitle = ({ children, className }: React.HTMLAttributes<HTMLHeadingElement> & { children: React.ReactNode }) => {
  return <h3 className={className}>{children}</h3>
}

Drawer.Title = DrawerTitle

const DrawerBody = ({ children, ...rest }: HTMLAttributes<HTMLDivElement>) => {
  return <div {...rest}>{children}</div>
}

Drawer.Body = DrawerBody

const DrawerFooter = ({ children, className }: { children: React.ReactNode; className?: string }) => {
  return <footer className={cn('bg-white', className)}>{children}</footer>
}

Drawer.Footer = DrawerFooter
